import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  HandCoins, 
  Users, 
  TrendingUp, 
  Gift, 
  Plus, 
  Wallet,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  XCircle,
  Loader2,
  Euro,
  CreditCard,
  FileText,
  Star
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const ClientAffiliatePage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [affiliateStatus, setAffiliateStatus] = useState(null);
  const [isAffiliate, setIsAffiliate] = useState(false);
  
  // Modals
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  
  // Forms
  const [joinForm, setJoinForm] = useState({
    accept_terms: false,
    preferred_commission_type: 'credit',
    bank_iban: '',
    bank_bic: ''
  });
  
  const [leadForm, setLeadForm] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    service_interest: '',
    notes: ''
  });
  
  const [convertAmount, setConvertAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAffiliateStatus();
  }, []);

  const fetchAffiliateStatus = async () => {
    try {
      const res = await api.get('/affiliates/client/my-affiliate-status');
      setIsAffiliate(res.data.is_affiliate);
      setAffiliateStatus(res.data.affiliate);
    } catch (error) {
      console.error('Error fetching affiliate status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinProgram = async () => {
    if (!joinForm.accept_terms) {
      toast.error('Veuillez accepter les conditions du programme');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/affiliates/client/become-affiliate', joinForm);
      toast.success('Bienvenue dans le programme d&apos;affiliation !');
      setShowJoinModal(false);
      fetchAffiliateStatus();
    } catch (error) {
      toast.error('Erreur lors de l&apos;inscription');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitLead = async () => {
    if (!leadForm.client_name || !leadForm.client_email || !leadForm.service_interest) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/affiliates/client/submit-lead', leadForm);
      toast.success('Lead soumis avec succès !');
      setShowLeadModal(false);
      setLeadForm({ client_name: '', client_email: '', client_phone: '', service_interest: '', notes: '' });
      fetchAffiliateStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConvertToCredit = async () => {
    const amount = parseFloat(convertAmount);
    if (!amount || amount <= 0) {
      toast.error('Montant invalide');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/affiliates/client/convert-to-credit', { amount });
      toast.success(`${amount}€ convertis en crédit service !`);
      setShowConvertModal(false);
      setConvertAmount('');
      fetchAffiliateStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la conversion');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: { bg: 'bg-blue-100 text-blue-700', label: 'Nouveau' },
      pending: { bg: 'bg-yellow-100 text-yellow-700', label: 'En attente' },
      contacted: { bg: 'bg-purple-100 text-purple-700', label: 'Contacté' },
      qualified: { bg: 'bg-cyan-100 text-cyan-700', label: 'Qualifié' },
      won: { bg: 'bg-emerald-100 text-emerald-700', label: 'Gagné' },
      lost: { bg: 'bg-red-100 text-red-700', label: 'Perdu' }
    };
    const style = styles[status] || styles.pending;
    return <Badge className={style.bg}>{style.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00d4aa]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 px-6 md:px-8 pb-6 md:pb-8",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}
        style={{ paddingTop: "130px" }}
      >
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-[#1a365d] flex items-center gap-3">
            <HandCoins className="w-8 h-8 text-[#f59e0b]" />
            Programme Affiliation
          </h1>
          <p className="text-[#64748b] mt-1">
            Recommandez nos services et gagnez des commissions
          </p>
        </div>

        {!isAffiliate ? (
          // Not yet an affiliate - Show join CTA
          <div className="max-w-3xl mx-auto">
            <Card className="card overflow-hidden">
              <div className="bg-gradient-to-br from-amber-500 to-orange-500 p-8 text-white">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                    <Gift className="w-8 h-8" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Devenez Apporteur d'Affaires</h2>
                    <p className="opacity-90">Gagnez 10% de commission sur chaque client recommandé</p>
                  </div>
                </div>
              </div>
              
              <CardContent className="p-8">
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="text-center p-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <Users className="w-6 h-6 text-emerald-600" />
                    </div>
                    <h3 className="font-semibold text-[#1a365d] mb-1">Recommandez</h3>
                    <p className="text-sm text-[#64748b]">Partagez nos services avec vos contacts</p>
                  </div>
                  
                  <div className="text-center p-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <TrendingUp className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="font-semibold text-[#1a365d] mb-1">Suivez</h3>
                    <p className="text-sm text-[#64748b]">Visualisez vos leads et leur progression</p>
                  </div>
                  
                  <div className="text-center p-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <Euro className="w-6 h-6 text-amber-600" />
                    </div>
                    <h3 className="font-semibold text-[#1a365d] mb-1">Gagnez</h3>
                    <p className="text-sm text-[#64748b]">10% en crédit service ou paiement</p>
                  </div>
                </div>
                
                <div className="bg-[#f8fafc] rounded-xl p-6 mb-6">
                  <h4 className="font-semibold text-[#1a365d] mb-3 flex items-center gap-2">
                    <Star className="w-5 h-5 text-amber-500" />
                    Avantages du programme
                  </h4>
                  <ul className="space-y-2 text-sm text-[#64748b]">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      Commission de 10% sur les projets signés
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      Choix: crédit service ou paiement bancaire
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      Suivi en temps réel de vos recommandations
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      Aucun engagement, inscrivez-vous gratuitement
                    </li>
                  </ul>
                </div>
                
                <Button 
                  onClick={() => setShowJoinModal(true)}
                  className="w-full h-12 text-lg"
                  style={{ backgroundColor: theme.colors?.accent }}
                >
                  <Gift className="w-5 h-5 mr-2" />
                  Rejoindre le programme
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : (
          // Is affiliate - Show dashboard
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <Card className="card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#64748b]">Total Gains</p>
                      <p className="text-2xl font-bold text-[#1a365d]">
                        {(affiliateStatus?.total_earnings || 0).toLocaleString('fr-FR')}€
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                      <Euro className="w-6 h-6 text-emerald-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#64748b]">En attente</p>
                      <p className="text-2xl font-bold text-amber-600">
                        {(affiliateStatus?.pending_earnings || 0).toLocaleString('fr-FR')}€
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                      <Clock className="w-6 h-6 text-amber-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#64748b]">Crédit Service</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {(affiliateStatus?.credit_balance || 0).toLocaleString('fr-FR')}€
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <CreditCard className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#64748b]">Leads soumis</p>
                      <p className="text-2xl font-bold text-[#1a365d]">
                        {affiliateStatus?.opportunities?.length || 0}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-violet-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-3 mb-8">
              <Button 
                onClick={() => setShowLeadModal(true)}
                style={{ backgroundColor: theme.colors?.accent }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Soumettre un lead
              </Button>
              
              {(affiliateStatus?.total_earnings || 0) > 0 && (
                <Button variant="outline" onClick={() => setShowConvertModal(true)}>
                  <Wallet className="w-4 h-4 mr-2" />
                  Convertir en crédit
                </Button>
              )}
            </div>

            {/* Leads Table */}
            <Card className="card">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#1a365d]">
                  Mes Recommandations
                </CardTitle>
              </CardHeader>
              <CardContent>
                {affiliateStatus?.opportunities?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Contact</TableHead>
                          <TableHead>Service</TableHead>
                          <TableHead>Statut</TableHead>
                          <TableHead>Commission</TableHead>
                          <TableHead>Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {affiliateStatus.opportunities.map((opp) => (
                          <TableRow key={opp.id}>
                            <TableCell>
                              <div>
                                <p className="font-medium text-[#1a365d]">{opp.client_name}</p>
                                <p className="text-xs text-[#64748b]">{opp.client_email}</p>
                              </div>
                            </TableCell>
                            <TableCell>{opp.service_interest || opp.service}</TableCell>
                            <TableCell>{getStatusBadge(opp.status)}</TableCell>
                            <TableCell>
                              {opp.commission_amount ? (
                                <span className="font-mono font-medium text-emerald-600">
                                  {opp.commission_amount.toLocaleString('fr-FR')}€
                                </span>
                              ) : (
                                <span className="text-[#94a3b8]">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-[#64748b] text-sm">
                              {new Date(opp.created_at).toLocaleDateString('fr-FR')}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-12 text-[#64748b]">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="mb-4">Aucune recommandation pour le moment</p>
                    <Button onClick={() => setShowLeadModal(true)} style={{ backgroundColor: theme.colors?.accent }}>
                      <Plus className="w-4 h-4 mr-2" />
                      Soumettre votre premier lead
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>

      {/* Join Program Modal */}
      <Dialog open={showJoinModal} onOpenChange={setShowJoinModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ color: theme.colors?.primary }}>
              Rejoindre le programme
            </DialogTitle>
            <DialogDescription>
              Inscrivez-vous gratuitement pour commencer à gagner des commissions
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Type de rémunération préféré</Label>
              <Select 
                value={joinForm.preferred_commission_type}
                onValueChange={(v) => setJoinForm({...joinForm, preferred_commission_type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="credit">Crédit service (recommandé)</SelectItem>
                  <SelectItem value="payment">Paiement bancaire</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {joinForm.preferred_commission_type === 'payment' && (
              <>
                <div className="space-y-2">
                  <Label>IBAN</Label>
                  <Input
                    value={joinForm.bank_iban}
                    onChange={(e) => setJoinForm({...joinForm, bank_iban: e.target.value})}
                    placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                  />
                </div>
                <div className="space-y-2">
                  <Label>BIC</Label>
                  <Input
                    value={joinForm.bank_bic}
                    onChange={(e) => setJoinForm({...joinForm, bank_bic: e.target.value})}
                    placeholder="BNPAFRPP"
                  />
                </div>
              </>
            )}
            
            <div className="flex items-start gap-3 p-4 bg-[#f8fafc] rounded-xl">
              <Checkbox 
                id="accept_terms"
                checked={joinForm.accept_terms}
                onCheckedChange={(checked) => setJoinForm({...joinForm, accept_terms: checked})}
              />
              <label htmlFor="accept_terms" className="text-sm text-[#64748b] cursor-pointer">
                J&apos;accepte les conditions du programme d&apos;affiliation et m&apos;engage à respecter l&apos;éthique commerciale
              </label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowJoinModal(false)}>Annuler</Button>
            <Button 
              onClick={handleJoinProgram} 
              disabled={submitting || !joinForm.accept_terms}
              style={{ backgroundColor: theme.colors?.accent }}
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Rejoindre
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submit Lead Modal */}
      <Dialog open={showLeadModal} onOpenChange={setShowLeadModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ color: theme.colors?.primary }}>
              Soumettre un lead
            </DialogTitle>
            <DialogDescription>
              Recommandez un contact intéressé par nos services
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nom du contact *</Label>
              <Input
                value={leadForm.client_name}
                onChange={(e) => setLeadForm({...leadForm, client_name: e.target.value})}
                placeholder="Jean Dupont"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={leadForm.client_email}
                onChange={(e) => setLeadForm({...leadForm, client_email: e.target.value})}
                placeholder="jean@entreprise.fr"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Téléphone</Label>
              <Input
                value={leadForm.client_phone}
                onChange={(e) => setLeadForm({...leadForm, client_phone: e.target.value})}
                placeholder="06 XX XX XX XX"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Service intéressé *</Label>
              <Select 
                value={leadForm.service_interest}
                onValueChange={(v) => setLeadForm({...leadForm, service_interest: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="website">Création de site web</SelectItem>
                  <SelectItem value="branding">Identité visuelle / Branding</SelectItem>
                  <SelectItem value="video">Production vidéo</SelectItem>
                  <SelectItem value="marketing">Marketing digital</SelectItem>
                  <SelectItem value="seo">Référencement SEO</SelectItem>
                  <SelectItem value="social">Community management</SelectItem>
                  <SelectItem value="copilote">Abonnement COPILOTE</SelectItem>
                  <SelectItem value="other">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Notes (optionnel)</Label>
              <Textarea
                value={leadForm.notes}
                onChange={(e) => setLeadForm({...leadForm, notes: e.target.value})}
                placeholder="Contexte, besoins spécifiques..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLeadModal(false)}>Annuler</Button>
            <Button 
              onClick={handleSubmitLead} 
              disabled={submitting}
              style={{ backgroundColor: theme.colors?.accent }}
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Soumettre
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to Credit Modal */}
      <Dialog open={showConvertModal} onOpenChange={setShowConvertModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ color: theme.colors?.primary }}>
              Convertir en crédit service
            </DialogTitle>
            <DialogDescription>
              Transformez vos gains en crédit pour vos projets
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-4 bg-[#f8fafc] rounded-xl">
              <p className="text-sm text-[#64748b] mb-1">Gains disponibles</p>
              <p className="text-2xl font-bold text-[#1a365d]">
                {((affiliateStatus?.total_earnings || 0) - (affiliateStatus?.withdrawn_amount || 0)).toLocaleString('fr-FR')}€
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Montant à convertir (€)</Label>
              <Input
                type="number"
                value={convertAmount}
                onChange={(e) => setConvertAmount(e.target.value)}
                placeholder="100"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvertModal(false)}>Annuler</Button>
            <Button 
              onClick={handleConvertToCredit} 
              disabled={submitting}
              style={{ backgroundColor: theme.colors?.accent }}
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Convertir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientAffiliatePage;
